import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Masks } from "react-native-mask-input";
import { z } from "zod";

import { useSearchBridgePayers } from "@/api/generated";
import {
  type BridgePayerSearchResult,
  type InsuranceCardReaderOutput,
  VerificationStatus,
} from "@/api/generated/models";
import { useUpdateUserProfile } from "@/api/mutations/userProfile";
import { useGetUserProfile } from "@/api/queries/userProfile";
import {
  Button,
  Checkbox,
  Grid,
  Input,
  MaskedInput,
  XStack,
  YStack,
  getGridLayout,
} from "@/components";
import ErrorLabel from "@/components/forms/ErrorLabel";
import SkipOrSubmit from "@/conversation/AskUserQuestionsPanel/SkipOrSubmit";
import type { InsuranceVerifyReason } from "@/conversation/PreClinicSteps/InsuranceVerificationStep";
import { CarrierSearchField } from "@/forms/CarrierSearchField";
import {
  EMPTY_CARRIER,
  carrierMemberIdRefinement,
  carrierSchema,
} from "@/forms/carrierSchema";
import { dateOfBirthZodString } from "@/forms/ProfileForm";

const normalize = (value: string) =>
  value.toLowerCase().replace(/\s+/g, " ").trim();

export const findConfidentPayerMatch = (
  ocrCarrierName: string,
  payers: BridgePayerSearchResult[],
): BridgePayerSearchResult | null => {
  const normalizedOcr = normalize(ocrCarrierName);
  if (!normalizedOcr) {
    return null;
  }
  return (
    payers.find((payer) => normalize(payer.name) === normalizedOcr) ?? null
  );
};

const CARRIER_UNMATCHED_ERROR =
  "We couldn't match your insurance carrier — please select the best option.";

const CARRIER_REQUIRED_ERROR =
  "Please select an insurance carrier from the list";

const VERIFY_ERROR_MESSAGES: Record<InsuranceVerifyReason, string> = {
  unverified: "Your insurance could not be verified.",
  pending: "It's taking a long time to confirm your insurance.",
};

const manualInsuranceSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    dateOfBirth: dateOfBirthZodString,
    carrier: carrierSchema.refine((value) => value.bridgePayerId.length > 0, {
      message: CARRIER_REQUIRED_ERROR,
    }),
    memberId: z.string(),
    groupNumber: z.string(),
    userIsPolicyHolder: z.boolean(),
  })
  .superRefine(carrierMemberIdRefinement);

type ManualInsuranceForm = z.infer<typeof manualInsuranceSchema>;

export default function InsuranceManualStep({
  insurance,
  onTakePhoto,
  onSubmitted,
  errorReason,
  onProceedWithoutInsurance,
}: {
  insurance: InsuranceCardReaderOutput | null;
  onTakePhoto: () => void;
  onSubmitted: () => void;
  errorReason?: InsuranceVerifyReason | null;
  onProceedWithoutInsurance: () => void;
}) {
  const { data: profile, isLoading: profileLoading } = useGetUserProfile();
  const { mutate: updateUserProfile, isPending } = useUpdateUserProfile();
  const isIdVerified =
    profile?.idVerification?.status === VerificationStatus.approved;

  const [carrierError, setCarrierError] = useState<string | undefined>();

  const ocrCarrier = insurance?.carrier_name ?? "";
  const {
    data: matchResults = [],
    isFetching: isMatching,
    isFetched: matchFetched,
  } = useSearchBridgePayers(
    { query: ocrCarrier, limit: 30 },
    { query: { enabled: ocrCarrier.length > 0 } },
  );

  const { control, handleSubmit, reset } = useForm<ManualInsuranceForm>({
    resolver: zodResolver(manualInsuranceSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      carrier: EMPTY_CARRIER,
      memberId: "",
      groupNumber: "",
      userIsPolicyHolder: true,
    },
  });

  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || profileLoading) {
      return;
    }
    if (ocrCarrier && (!matchFetched || isMatching)) {
      return;
    }
    let carrier = EMPTY_CARRIER;
    if (ocrCarrier) {
      const match = findConfidentPayerMatch(ocrCarrier, matchResults);
      if (match) {
        carrier = {
          carrierName: match.name,
          bridgePayerId: match.id,
          requiresMemberId: match.member_id,
        };
      } else {
        carrier = {
          carrierName: ocrCarrier,
          bridgePayerId: "",
          requiresMemberId: true,
        };
        setCarrierError(CARRIER_UNMATCHED_ERROR);
      }
    } else if (profile) {
      carrier = {
        carrierName: profile.carrierName ?? "",
        bridgePayerId: profile.bridgePayerId ?? "",
        requiresMemberId: true,
      };
    }
    reset({
      firstName: profile?.firstName ?? "",
      lastName: profile?.lastName ?? "",
      dateOfBirth: profile?.dateOfBirth ?? "",
      carrier,
      memberId: insurance?.member_id ?? profile?.memberId ?? "",
      groupNumber: insurance?.group_number ?? profile?.groupNumber ?? "",
      userIsPolicyHolder: profile?.userIsPolicyHolder ?? true,
    });
    setSeeded(true);
  }, [
    seeded,
    profileLoading,
    profile,
    ocrCarrier,
    matchFetched,
    isMatching,
    matchResults,
    insurance,
    reset,
  ]);

  const onSubmit = handleSubmit(({ carrier, ...rest }) => {
    updateUserProfile(
      {
        data: {
          ...rest,
          carrierName: carrier.carrierName,
          bridgePayerId: carrier.bridgePayerId,
        },
      },
      { onSuccess: onSubmitted },
    );
  });

  const isLoading = !seeded;

  return (
    <>
      {errorReason && (
        <ErrorLabel
          paddingHorizontal="$150"
          paddingBottom="$200"
          alignItems="flex-start"
          textProps={{ flex: 1 }}
        >
          {VERIFY_ERROR_MESSAGES[errorReason]} Please confirm your information.
        </ErrorLabel>
      )}
      <Grid
        paddingHorizontal="$150"
        opacity={isLoading ? 0.5 : 1}
        pointerEvents={isLoading ? "none" : "auto"}
      >
        <Controller
          control={control}
          name="firstName"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input.Controlled
              label="First name"
              readonly={isIdVerified}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="lastName"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input.Controlled
              label="Last name"
              readonly={isIdVerified}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="dateOfBirth"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <MaskedInput.Controlled
              label="Date of birth"
              readonly={isIdVerified}
              mask={Masks.DATE_MMDDYYYY}
              placeholder="mm/dd/yyyy"
              inputMode="numeric"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="carrier"
          render={({ field: { value, onChange, onBlur }, fieldState }) => (
            <CarrierSearchField
              value={value}
              onChange={(next) => {
                onChange(next);
                setCarrierError(undefined);
              }}
              onBlur={onBlur}
              errorText={carrierError ?? fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="memberId"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input.Controlled
              label="Member ID"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="groupNumber"
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <Input.Controlled
              label="Group number"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              errorText={fieldState.error?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
      </Grid>
      <YStack
        gap="$150"
        paddingHorizontal="$150"
        marginTop="$250"
        paddingBottom="$150"
      >
        <Controller
          control={control}
          name="userIsPolicyHolder"
          render={({ field: { onChange, value } }) => (
            <Checkbox
              labelProps={{ fontSize: "$sm", fontWeight: "normal" }}
              rootProps={{ width: "100%" }}
              label="I am the insurance policy holder"
              checked={value}
              disabled={isLoading}
              onCheckedChange={(checked) => onChange(checked === true)}
            />
          )}
        />
        <XStack justifyContent="flex-end" alignItems="center" gap="$200">
          {!errorReason && !insurance && (
            <Button mode="secondary" onPress={onTakePhoto}>
              Take photo of card
            </Button>
          )}
          {errorReason && (
            <Button
              mode="secondary"
              dataTrackingId="insurance-manual__clicked-proceed-without-insurance"
              onPress={onProceedWithoutInsurance}
            >
              Proceed without insurance
            </Button>
          )}
          <SkipOrSubmit
            showSkip={false}
            showSubmit
            submitDisabled={isPending || isLoading}
            isLast
            onSkip={onTakePhoto}
            onSubmit={onSubmit}
          />
        </XStack>
      </YStack>
    </>
  );
}
